import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { pagePaperSx, pageTabsSx, lato } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  LinearProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Button,
  Tabs,
  Tab,
  SelectChangeEvent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../config/api';
import { resolveCheckTypeEnumLabel, type AdminUiLabelKey } from '../../constants/adminUiLabels';
import { useAdminUiLabels } from '../../context/AdminUiLabelsContext';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CheckReport {
  check_id: string;
  check_name: string;
  check_type: string;
  location_name: string;
  due_date: string;
  check_status: string;
  completion_percentage: number;
  total_assigned_users: number;
  completed_users: number;
  overdue_users: number;
  pending_users: number;
}

interface UserPerformance {
  user_id: string;
  first_name: string;
  last_name: string;
  total_checks: number;
  completed_checks: number;
  overdue_checks: number;
  completion_rate: number;
  last_completed_check: string;
}

interface LocationStats {
  location_id: string;
  location_name: string;
  total_checks: number;
  completed_checks: number;
  overdue_checks: number;
  completion_rate: number;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const ALL_CHECK_TYPES = ['SAFETY', 'DVLA', 'LOAN', 'LOAN_RETURN', 'DELIVERY', 'OUTGOING_DELIVERY', 'LOCATION'];

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
];

const STATUS_COLORS: Record<string, string> = {
  completed: '#4caf50',
  overdue: '#f44336',
  pending: '#ff9800',
  in_progress: '#2196f3',
};

const CHECK_TYPE_COLORS = [
  '#6366f1',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#3b82f6',
  '#ec4899',
  '#14b8a6',
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function normalizeCheckType(type?: string | null): string {
  if (type && type.trim()) return type.toUpperCase();
  return 'SAFETY';
}

function checkTypeLabel(type?: string | null, overrides?: Partial<Record<AdminUiLabelKey, string>>) {
  return resolveCheckTypeEnumLabel(type, overrides ?? {});
}

function checkTypeColor(type?: string | null) {
  const normalized = normalizeCheckType(type);
  const idx = ALL_CHECK_TYPES.indexOf(normalized);
  return CHECK_TYPE_COLORS[idx >= 0 ? idx % CHECK_TYPE_COLORS.length : 0];
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case 'completed': return 'success';
    case 'overdue': return 'error';
    case 'pending': return 'warning';
    case 'in_progress': return 'info';
    default: return 'default';
  }
}

function progressColor(pct: number): 'success' | 'error' | 'warning' {
  if (pct === 100) return 'success';
  if (pct < 50) return 'error';
  return 'warning';
}

function formatDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

type SortDir = 'asc' | 'desc';

function stableSort<T>(arr: T[], comparator: (a: T, b: T) => number): T[] {
  return arr
    .map((el, idx) => ({ el, idx }))
    .sort((a, b) => {
      const c = comparator(a.el, b.el);
      return c !== 0 ? c : a.idx - b.idx;
    })
    .map(({ el }) => el);
}

function getComparator<T>(order: SortDir, orderBy: keyof T): (a: T, b: T) => number {
  return order === 'desc'
    ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : b[orderBy] > a[orderBy] ? 1 : 0)
    : (a, b) => (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
}

// ─── Excel export ──────────────────────────────────────────────────────────────

async function exportToExcel(
  checks: CheckReport[],
  users: UserPerformance[],
  locations: LocationStats[],
  overrides: Partial<Record<AdminUiLabelKey, string>> = {},
) {
  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();

  const addSheet = (name: string, columns: { header: string; key: string; width?: number }[], rows: object[]) => {
    const ws = wb.addWorksheet(name);
    ws.columns = columns;
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3E8FF' } };
    rows.forEach(r => ws.addRow(r));
  };

  addSheet(
    'Check Overview',
    [
      { header: 'Check Name', key: 'check_name', width: 30 },
      { header: 'Type', key: 'check_type', width: 18 },
      { header: 'Location', key: 'location_name', width: 22 },
      { header: 'Due Date', key: 'due_date', width: 14 },
      { header: 'Status', key: 'check_status', width: 14 },
      { header: 'Completion %', key: 'completion_percentage', width: 14 },
      { header: 'Assigned', key: 'total_assigned_users', width: 10 },
      { header: 'Completed', key: 'completed_users', width: 10 },
      { header: 'Overdue', key: 'overdue_users', width: 10 },
      { header: 'Pending', key: 'pending_users', width: 10 },
    ],
    checks.map(c => ({
      ...c,
      check_type: checkTypeLabel(c.check_type, overrides),
      due_date: formatDate(c.due_date),
      completion_percentage: Math.round(c.completion_percentage),
    })),
  );

  addSheet(
    'User Performance',
    [
      { header: 'Name', key: 'name', width: 24 },
      { header: 'Total Checks', key: 'total_checks', width: 14 },
      { header: 'Completed', key: 'completed_checks', width: 12 },
      { header: 'Overdue', key: 'overdue_checks', width: 12 },
      { header: 'Completion Rate %', key: 'completion_rate', width: 18 },
      { header: 'Last Completed', key: 'last_completed_check', width: 16 },
    ],
    users.map(u => ({
      name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || 'Unknown',
      total_checks: u.total_checks,
      completed_checks: u.completed_checks,
      overdue_checks: u.overdue_checks,
      completion_rate: Math.round(u.completion_rate),
      last_completed_check: formatDate(u.last_completed_check),
    })),
  );

  addSheet(
    'Location Statistics',
    [
      { header: 'Location', key: 'location_name', width: 26 },
      { header: 'Total Checks', key: 'total_checks', width: 14 },
      { header: 'Completed', key: 'completed_checks', width: 12 },
      { header: 'Overdue', key: 'overdue_checks', width: 12 },
      { header: 'Completion Rate %', key: 'completion_rate', width: 18 },
    ],
    locations.map(l => ({ ...l, completion_rate: Math.round(l.completion_rate) })),
  );

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `safety-check-reports-${dayjs().format('YYYY-MM-DD')}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main component ────────────────────────────────────────────────────────────

const Reports: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { adminUiLabels } = useAdminUiLabels();

  // raw data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkReports, setCheckReports] = useState<CheckReport[]>([]);
  const [userPerformance, setUserPerformance] = useState<UserPerformance[]>([]);
  const [locationStats, setLocationStats] = useState<LocationStats[]>([]);
  const [overdueAssetReturns, setOverdueAssetReturns] = useState(0);

  // filters
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // active tab
  const [tab, setTab] = useState(0);

  // sort state for checks table
  const [checksOrder, setChecksOrder] = useState<SortDir>('asc');
  const [checksOrderBy, setChecksOrderBy] = useState<keyof CheckReport>('due_date');
  const [usersOrder, setUsersOrder] = useState<SortDir>('desc');
  const [usersOrderBy, setUsersOrderBy] = useState<keyof UserPerformance>('completion_rate');
  const [locsOrder, setLocsOrder] = useState<SortDir>('desc');
  const [locsOrderBy, setLocsOrderBy] = useState<keyof LocationStats>('completion_rate');

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token');
      const headers = { Authorization: `Bearer ${token}` };

      const [checksRes, usersRes, locsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/checks/reports`, { headers }),
        axios.get(`${API_BASE_URL}/users/performance`, { headers }),
        axios.get(`${API_BASE_URL}/locations/stats`, { headers }),
      ]);

      if (Array.isArray(checksRes.data)) {
        setCheckReports(checksRes.data);
        setOverdueAssetReturns(0);
      } else {
        setCheckReports(Array.isArray(checksRes.data.reports) ? checksRes.data.reports : []);
        setOverdueAssetReturns(Number(checksRes.data.overdueAssetReturns) || 0);
      }
      setUserPerformance(usersRes.data);
      setLocationStats(locsRes.data);
    } catch {
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // derived: unique locations
  const locationOptions = useMemo(() => {
    const names = new Set(checkReports.map(c => c.location_name).filter(Boolean));
    return Array.from(names).sort();
  }, [checkReports]);

  // derived: filtered checks
  const filteredChecks = useMemo(() => {
    return checkReports.filter(c => {
      if (startDate && dayjs(c.due_date).isBefore(startDate, 'day')) return false;
      if (endDate && dayjs(c.due_date).isAfter(endDate, 'day')) return false;
      if (selectedTypes.length && !selectedTypes.includes(normalizeCheckType(c.check_type))) return false;
      if (selectedStatuses.length && !selectedStatuses.includes(c.check_status)) return false;
      if (selectedLocation && c.location_name !== selectedLocation) return false;
      return true;
    });
  }, [checkReports, startDate, endDate, selectedTypes, selectedStatuses, selectedLocation]);

  const hasActiveFilters =
    startDate !== null ||
    endDate !== null ||
    selectedTypes.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedLocation !== '';

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedLocation('');
  };

  // ─── Summary stats ────────────────────────────────────────────────────────────

  const totalChecks = filteredChecks.length;
  const completedCount = filteredChecks.filter(c => c.check_status === 'completed').length;
  const overdueCount = filteredChecks.filter(c => c.check_status === 'overdue').length + (hasActiveFilters ? 0 : overdueAssetReturns);
  const avgCompletionRate =
    filteredChecks.length === 0
      ? 0
      : Math.round(filteredChecks.reduce((s, c) => s + (Number(c.completion_percentage) || 0), 0) / filteredChecks.length);

  // ─── Chart data ───────────────────────────────────────────────────────────────

  const statusPieData = useMemo(() => {
    const counts: Record<string, number> = { completed: 0, overdue: 0, pending: 0, in_progress: 0 };
    filteredChecks.forEach(c => { counts[c.check_status] = (counts[c.check_status] || 0) + 1; });
    return STATUS_OPTIONS
      .map(s => ({ name: s.label, value: counts[s.value] || 0, color: STATUS_COLORS[s.value] }))
      .filter(d => d.value > 0);
  }, [filteredChecks]);

  const typeBarData = useMemo(() => {
    const map: Record<string, { completed: number; overdue: number; pending: number; in_progress: number }> = {};
    filteredChecks.forEach(c => {
      const t = normalizeCheckType(c.check_type);
      if (!map[t]) map[t] = { completed: 0, overdue: 0, pending: 0, in_progress: 0 };
      const key = c.check_status as keyof typeof map[string];
      if (key in map[t]) map[t][key]++;
    });
    return Object.entries(map).map(([type, counts]) => ({
      type: checkTypeLabel(type, adminUiLabels),
      ...counts,
    }));
  }, [filteredChecks, adminUiLabels]);

  const userBarData = useMemo(() => {
    return [...userPerformance]
      .sort((a, b) => b.completion_rate - a.completion_rate)
      .slice(0, 10)
      .map(u => ({
        name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || 'Unknown',
        rate: Math.round(u.completion_rate),
        total: u.total_checks,
        completed: u.completed_checks,
        overdue: u.overdue_checks,
      }));
  }, [userPerformance]);

  const locBarData = useMemo(() => {
    return [...locationStats]
      .sort((a, b) => b.completion_rate - a.completion_rate)
      .map(l => ({
        name: l.location_name,
        rate: Math.round(l.completion_rate),
        total: l.total_checks,
        completed: l.completed_checks,
        overdue: l.overdue_checks,
      }));
  }, [locationStats]);

  // ─── Sorted table data ────────────────────────────────────────────────────────

  const sortedChecks = useMemo(
    () => stableSort(filteredChecks, getComparator(checksOrder, checksOrderBy)),
    [filteredChecks, checksOrder, checksOrderBy],
  );

  const sortedUsers = useMemo(
    () => stableSort(userPerformance, getComparator(usersOrder, usersOrderBy)),
    [userPerformance, usersOrder, usersOrderBy],
  );

  const sortedLocs = useMemo(
    () => stableSort(locationStats, getComparator(locsOrder, locsOrderBy)),
    [locationStats, locsOrder, locsOrderBy],
  );

  const handleChecksSort = (col: keyof CheckReport) => {
    setChecksOrderBy(col);
    setChecksOrder(prev => (checksOrderBy === col && prev === 'asc' ? 'desc' : 'asc'));
  };
  const handleUsersSort = (col: keyof UserPerformance) => {
    setUsersOrderBy(col);
    setUsersOrder(prev => (usersOrderBy === col && prev === 'asc' ? 'desc' : 'asc'));
  };
  const handleLocsSort = (col: keyof LocationStats) => {
    setLocsOrderBy(col);
    setLocsOrder(prev => (locsOrderBy === col && prev === 'asc' ? 'desc' : 'asc'));
  };

  // ─── Loading / error states ───────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={<Button size="small" onClick={fetchReports}>Retry</Button>}>{error}</Alert>
      </Box>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 4 }}>
        {/* Header */}
        <PageHeader
          title="Reports"
          subtitle="Insights"
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={filtersOpen ? 'Hide Filters' : 'Show Filters'}>
                <IconButton onClick={() => setFiltersOpen(p => !p)} color={hasActiveFilters ? 'primary' : 'default'}>
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton onClick={() => exportToExcel(filteredChecks, userPerformance, locationStats, adminUiLabels)}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchReports}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />

        {/* Filter Bar */}
        {filtersOpen && (
          <Paper sx={{ ...pagePaperSx, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <FilterListIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontFamily={lato} fontWeight={600}>
                Filters
              </Typography>
              {hasActiveFilters && (
                <Chip
                  label="Clear all"
                  size="small"
                  icon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{ ml: 'auto' }}
                />
              )}
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              {/* Date range */}
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="From date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  maxDate={endDate ?? undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  minDate={startDate ?? undefined}
                />
              </Grid>

              {/* Check type */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Check Type</InputLabel>
                  <Select
                    multiple
                    value={selectedTypes}
                    onChange={(e: SelectChangeEvent<string[]>) =>
                      setSelectedTypes(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)
                    }
                    input={<OutlinedInput label="Check Type" />}
                    renderValue={sel => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(sel as string[]).map(v => (
                          <Chip key={v} label={checkTypeLabel(v, adminUiLabels)} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {ALL_CHECK_TYPES.map(t => (
                      <MenuItem key={t} value={t}>{checkTypeLabel(t, adminUiLabels)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    multiple
                    value={selectedStatuses}
                    onChange={(e: SelectChangeEvent<string[]>) =>
                      setSelectedStatuses(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)
                    }
                    input={<OutlinedInput label="Status" />}
                    renderValue={sel => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(sel as string[]).map(v => (
                          <Chip key={v} label={STATUS_OPTIONS.find(s => s.value === v)?.label ?? v} size="small" color={statusColor(v)} />
                        ))}
                      </Box>
                    )}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Location */}
              {locationOptions.length > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={selectedLocation}
                      onChange={e => setSelectedLocation(e.target.value)}
                      label="Location"
                    >
                      <MenuItem value="">All locations</MenuItem>
                      {locationOptions.map(l => (
                        <MenuItem key={l} value={l}>{l}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {hasActiveFilters && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredChecks.length} of {checkReports.length} checks
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2.5} mb={4}>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ ...pagePaperSx, p: 0, height: '100%', borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
              <CardHeader
                title="Total Checks"
                subheader={hasActiveFilters ? 'Filtered' : 'All checks'}
                avatar={<CheckCircleIcon color="primary" fontSize="small" />}
                sx={{ pb: 0.5 }}
                titleTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                subheaderTypographyProps={{ variant: 'caption' }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="h4" fontFamily={lato} fontWeight={700}>{totalChecks}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ ...pagePaperSx, p: 0, height: '100%', borderLeft: '3px solid', borderLeftColor: 'success.main' }}>
              <CardHeader
                title="Completed"
                subheader={totalChecks ? `${Math.round((completedCount / totalChecks) * 100)}% of total` : '—'}
                avatar={<CheckCircleIcon color="success" fontSize="small" />}
                sx={{ pb: 0.5 }}
                titleTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                subheaderTypographyProps={{ variant: 'caption' }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="h4" fontFamily={lato} fontWeight={700} color="success.main">{completedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ ...pagePaperSx, p: 0, height: '100%', borderLeft: '3px solid', borderLeftColor: 'error.main' }}>
              <CardHeader
                title="Overdue"
                subheader={hasActiveFilters ? 'Filtered checks' : 'Incl. asset returns'}
                avatar={<WarningIcon color="error" fontSize="small" />}
                sx={{ pb: 0.5 }}
                titleTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                subheaderTypographyProps={{ variant: 'caption' }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="h4" fontFamily={lato} fontWeight={700} color="error.main">{overdueCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ ...pagePaperSx, p: 0, height: '100%', borderLeft: '3px solid', borderLeftColor: 'info.main' }}>
              <CardHeader
                title="Avg Completion"
                subheader="Across filtered checks"
                avatar={<TrendingUpIcon color="info" fontSize="small" />}
                sx={{ pb: 0.5 }}
                titleTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                subheaderTypographyProps={{ variant: 'caption' }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="h4" fontFamily={lato} fontWeight={700}>{avgCompletionRate}%</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Row 1 */}
        <Grid container spacing={2.5} mb={3}>
          {/* Status donut */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ ...pagePaperSx }}>
              <Typography variant="subtitle1" fontWeight={600} fontFamily={lato} mb={2}>
                Status Distribution
              </Typography>
              {statusPieData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="text.secondary" variant="body2">No data</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {statusPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <ReTooltip formatter={(v: number, name: string) => [`${v} checks`, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Check type bar */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ ...pagePaperSx }}>
              <Typography variant="subtitle1" fontWeight={600} fontFamily={lato} mb={2}>
                Checks by Type
              </Typography>
              {typeBarData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 230 }}>
                  <Typography color="text.secondary" variant="body2">No data</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={typeBarData} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ReTooltip />
                    <Legend />
                    <Bar dataKey="completed" stackId="a" name="Completed" fill={STATUS_COLORS.completed} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="in_progress" stackId="a" name="In Progress" fill={STATUS_COLORS.in_progress} />
                    <Bar dataKey="pending" stackId="a" name="Pending" fill={STATUS_COLORS.pending} />
                    <Bar dataKey="overdue" stackId="a" name="Overdue" fill={STATUS_COLORS.overdue} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Charts Row 2 */}
        <Grid container spacing={2.5} mb={4}>
          {/* User performance */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ ...pagePaperSx }}>
              <Typography variant="subtitle1" fontWeight={600} fontFamily={lato} mb={2}>
                Top 10 Users by Completion Rate
              </Typography>
              {userBarData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="text.secondary" variant="body2">No data</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, userBarData.length * 36)}>
                  <BarChart
                    layout="vertical"
                    data={userBarData}
                    margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                    <ReTooltip formatter={(v: number) => [`${v}%`, 'Completion Rate']} />
                    <Bar dataKey="rate" name="Completion %" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Location performance */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ ...pagePaperSx }}>
              <Typography variant="subtitle1" fontWeight={600} fontFamily={lato} mb={2}>
                Location Completion Rates
              </Typography>
              {locBarData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="text.secondary" variant="body2">No data</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, locBarData.length * 36)}>
                  <BarChart
                    layout="vertical"
                    data={locBarData}
                    margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                    <ReTooltip formatter={(v: number) => [`${v}%`, 'Completion Rate']} />
                    <Bar dataKey="rate" name="Completion %" fill={theme.palette.secondary.main ?? '#6366f1'} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Tabbed detail tables */}
        <Paper sx={{ ...pagePaperSx }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={pageTabsSx}
            variant="fullWidth"
          >
            <Tab label={`Check Overview (${filteredChecks.length})`} />
            <Tab label={`User Performance (${userPerformance.length})`} />
            <Tab label={`Locations (${locationStats.length})`} />
          </Tabs>

          {/* ── Tab 0: Check overview ── */}
          {tab === 0 && (
            <>
              {filteredChecks.length === 0 ? (
                <Box p={4} textAlign="center">
                  <Typography color="text.secondary">No checks match the current filters.</Typography>
                </Box>
              ) : isMobile ? (
                <Box sx={{ p: 1 }}>
                  {sortedChecks.map(check => (
                    <Paper key={check.check_id} sx={{ p: 2, mb: 1.5, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>{check.check_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{check.location_name}</Typography>
                        </Box>
                        <Chip label={check.check_status.replace('_', ' ')} color={statusColor(check.check_status)} size="small" />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip label={checkTypeLabel(check.check_type, adminUiLabels)} size="small" sx={{ bgcolor: checkTypeColor(check.check_type), color: '#fff', fontSize: '0.68rem' }} />
                        <Typography variant="caption" color="text.secondary">Due: {formatDate(check.due_date)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={Number(check.completion_percentage) || 0} color={progressColor(Number(check.completion_percentage) || 0)} />
                        </Box>
                        <Typography variant="caption">{Math.round(Number(check.completion_percentage) || 0)}%</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {check.completed_users}/{check.total_assigned_users} completed
                        {check.overdue_users > 0 && <span style={{ color: theme.palette.error.main }}> · {check.overdue_users} overdue</span>}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel active={checksOrderBy === 'check_name'} direction={checksOrderBy === 'check_name' ? checksOrder : 'asc'} onClick={() => handleChecksSort('check_name')}>
                            Check Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>
                          <TableSortLabel active={checksOrderBy === 'location_name'} direction={checksOrderBy === 'location_name' ? checksOrder : 'asc'} onClick={() => handleChecksSort('location_name')}>
                            Location
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={checksOrderBy === 'due_date'} direction={checksOrderBy === 'due_date' ? checksOrder : 'asc'} onClick={() => handleChecksSort('due_date')}>
                            Due Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>
                          <TableSortLabel active={checksOrderBy === 'completion_percentage'} direction={checksOrderBy === 'completion_percentage' ? checksOrder : 'asc'} onClick={() => handleChecksSort('completion_percentage')}>
                            Completion
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Assigned Users</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedChecks.map(check => (
                        <TableRow key={check.check_id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{check.check_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={checkTypeLabel(check.check_type, adminUiLabels)}
                              size="small"
                              sx={{ bgcolor: checkTypeColor(check.check_type), color: '#fff', fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell>{check.location_name || '—'}</TableCell>
                          <TableCell>{formatDate(check.due_date)}</TableCell>
                          <TableCell>
                            <Chip label={check.check_status.replace('_', ' ')} color={statusColor(check.check_status)} size="small" />
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress variant="determinate" value={Number(check.completion_percentage) || 0} color={progressColor(Number(check.completion_percentage) || 0)} sx={{ height: 6, borderRadius: 3 }} />
                              </Box>
                              <Typography variant="caption" sx={{ minWidth: 32 }}>
                                {Math.round(Number(check.completion_percentage) || 0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{check.completed_users}/{check.total_assigned_users}</Typography>
                            {check.overdue_users > 0 && (
                              <Typography variant="caption" color="error">{check.overdue_users} overdue</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* ── Tab 1: User Performance ── */}
          {tab === 1 && (
            <>
              {userPerformance.length === 0 ? (
                <Box p={4} textAlign="center"><Typography color="text.secondary">No user data available.</Typography></Box>
              ) : isMobile ? (
                <Box sx={{ p: 1 }}>
                  {sortedUsers.map(user => (
                    <Paper key={user.user_id} sx={{ p: 2, mb: 1.5, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        {`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || 'Unknown User'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption">Total: <strong>{user.total_checks}</strong></Typography>
                        <Typography variant="caption" color="success.main">Completed: <strong>{user.completed_checks}</strong></Typography>
                        <Typography variant="caption" color="error.main">Overdue: <strong>{user.overdue_checks}</strong></Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={user.completion_rate} color={progressColor(user.completion_rate)} />
                        </Box>
                        <Typography variant="caption">{Math.round(user.completion_rate)}%</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Last completed: {formatDate(user.last_completed_check)}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>
                          <TableSortLabel active={usersOrderBy === 'total_checks'} direction={usersOrderBy === 'total_checks' ? usersOrder : 'asc'} onClick={() => handleUsersSort('total_checks')}>
                            Total
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={usersOrderBy === 'completed_checks'} direction={usersOrderBy === 'completed_checks' ? usersOrder : 'asc'} onClick={() => handleUsersSort('completed_checks')}>
                            Completed
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={usersOrderBy === 'overdue_checks'} direction={usersOrderBy === 'overdue_checks' ? usersOrder : 'asc'} onClick={() => handleUsersSort('overdue_checks')}>
                            Overdue
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={usersOrderBy === 'completion_rate'} direction={usersOrderBy === 'completion_rate' ? usersOrder : 'asc'} onClick={() => handleUsersSort('completion_rate')}>
                            Completion Rate
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Last Completed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedUsers.map(user => (
                        <TableRow key={user.user_id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || 'Unknown User'}
                          </TableCell>
                          <TableCell>{user.total_checks}</TableCell>
                          <TableCell>{user.completed_checks}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color={user.overdue_checks > 0 ? 'error' : 'inherit'}>
                              {user.overdue_checks}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress variant="determinate" value={user.completion_rate} color={progressColor(user.completion_rate)} sx={{ height: 6, borderRadius: 3 }} />
                              </Box>
                              <Typography variant="caption" sx={{ minWidth: 36 }}>
                                {Math.round(user.completion_rate)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{formatDate(user.last_completed_check)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* ── Tab 2: Locations ── */}
          {tab === 2 && (
            <>
              {locationStats.length === 0 ? (
                <Box p={4} textAlign="center"><Typography color="text.secondary">No location data available.</Typography></Box>
              ) : isMobile ? (
                <Box sx={{ p: 1 }}>
                  {sortedLocs.map(loc => (
                    <Paper key={loc.location_id} sx={{ p: 2, mb: 1.5, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>{loc.location_name}</Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption">Total: <strong>{loc.total_checks}</strong></Typography>
                        <Typography variant="caption" color="success.main">Completed: <strong>{loc.completed_checks}</strong></Typography>
                        <Typography variant="caption" color="error.main">Overdue: <strong>{loc.overdue_checks}</strong></Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={loc.completion_rate} color={progressColor(loc.completion_rate)} />
                        </Box>
                        <Typography variant="caption">{Math.round(loc.completion_rate)}%</Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel active={locsOrderBy === 'location_name'} direction={locsOrderBy === 'location_name' ? locsOrder : 'asc'} onClick={() => handleLocsSort('location_name')}>
                            Location
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={locsOrderBy === 'total_checks'} direction={locsOrderBy === 'total_checks' ? locsOrder : 'asc'} onClick={() => handleLocsSort('total_checks')}>
                            Total
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={locsOrderBy === 'completed_checks'} direction={locsOrderBy === 'completed_checks' ? locsOrder : 'asc'} onClick={() => handleLocsSort('completed_checks')}>
                            Completed
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={locsOrderBy === 'overdue_checks'} direction={locsOrderBy === 'overdue_checks' ? locsOrder : 'asc'} onClick={() => handleLocsSort('overdue_checks')}>
                            Overdue
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={locsOrderBy === 'completion_rate'} direction={locsOrderBy === 'completion_rate' ? locsOrder : 'asc'} onClick={() => handleLocsSort('completion_rate')}>
                            Completion Rate
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedLocs.map(loc => (
                        <TableRow key={loc.location_id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{loc.location_name}</TableCell>
                          <TableCell>{loc.total_checks}</TableCell>
                          <TableCell>{loc.completed_checks}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color={loc.overdue_checks > 0 ? 'error' : 'inherit'}>
                              {loc.overdue_checks}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress variant="determinate" value={loc.completion_rate} color={progressColor(loc.completion_rate)} sx={{ height: 6, borderRadius: 3 }} />
                              </Box>
                              <Typography variant="caption" sx={{ minWidth: 36 }}>
                                {Math.round(loc.completion_rate)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
